import { resetAuthRefreshLatch, tripAuthRefreshLatch } from '@artic/api-client';
import {
  AUTH_COOKIE,
  AUTH_LOGOUT_GENERATION_COOKIE,
  clearAuthLogoutIntent,
  clearAuthCookies,
  hasAuthRefreshAttempt,
  hasAuthLogoutIntent,
  readAuthSessionCookies,
  readCookie,
  readJwtSubject,
  setAuthSessionCookies,
} from '@artic/ui/lib/auth-cookies';
import { clearAppLocalStorage } from '@artic/ui/lib/supabase-cookie-storage';
import { useFeatures } from '@saas-ui-pro/feature-flags';
import * as Sentry from '@sentry/react';
import type { Session } from '@supabase/supabase-js';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { useEffect, useRef, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import AppRouter from '~/AppRouter';
import { partnerPicker } from '~/atoms/partnerPicker';
import sessionStorage from '~/atoms/sessionStorage';
import { isInitialAuthCallback, supabase } from '~/utils/supabaseClient';

import './style.css';

/**
 * Safety timeout — if Supabase init hangs, don't leave the user on a blank
 * screen. With processLock (see supabaseClient.ts) init is near-synchronous,
 * so 2s is a generous fail-open window.
 */
const AUTH_READY_TIMEOUT_MS = 2_000;
const SESSION_ADOPTION_RETRY_DELAYS_MS = [100, 500, 2_000, 10_000] as const;

interface SessionAdoptionContext {
  previousToken: string;
  previousRefreshToken: string;
  previousUserId: string;
  logoutGeneration: string | undefined;
}

function isTerminalSessionAdoptionError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const status = (error as Record<string, unknown>)['status'];
  return (
    typeof status === 'number' &&
    status >= 400 &&
    status < 500 &&
    status !== 408 &&
    status !== 409 &&
    status !== 429
  );
}

function App() {
  const [isAuthReady, setIsAuthReady] = useState(false);
  const isAuthReadyRef = useRef(false);
  const lastSeenAccessTokenRef = useRef<string | null>(null);
  const lastSeenRefreshTokenRef = useRef<string | null>(null);
  const lastSeenUserIdRef = useRef<string | null>(null);
  const setSession = useSetRecoilState(sessionStorage);
  const selectedPartner = useRecoilValue(partnerPicker);
  const { update } = useIntercom();
  const features = useFeatures();

  const clearUser = () => {
    update();
    clearAppLocalStorage();
    Sentry.setUser(null);
  };

  const setUser = (session: Session) => {
    const userId = session.user.id;
    const email = session.user.email;
    const name = session.user.user_metadata?.first_name
      ? `${session.user.user_metadata?.first_name} ${session.user.user_metadata?.last_name}`
      : undefined;
    update({
      userId,
      email,
      name,
    });
    Sentry.setUser({
      id: userId,
      ...(email ? { email } : {}),
      ...(name ? { username: name } : {}),
    });
    const userIdentify = {
      userId,
      admin: false,
    };
    if (
      email?.includes('@trunk.works') ||
      email?.includes('@artic.works') ||
      email?.includes('@redfiredigital.uk')
    ) {
      userIdentify.admin = true;
    }
    features.identify(userIdentify);
  };

  // Tag the active partner on the Sentry scope so frontend errors are
  // attributable to which partner the user was acting on at the time. The
  // partnerPicker atom is cookie- and localStorage-backed, so this re-fires
  // when the user switches partner via the picker.
  useEffect(() => {
    if (selectedPartner) {
      Sentry.setTag('partner_key', selectedPartner.partner_key);
      Sentry.setTag('partner_name', selectedPartner.partner_name);
    } else {
      Sentry.setTag('partner_key', undefined);
      Sentry.setTag('partner_name', undefined);
    }
  }, [selectedPartner]);

  const cookieDomain = import.meta.env.VITE_AUTH_COOKIE_DOMAIN || undefined;

  const syncCookies = (session: Session, previousRefreshToken?: string) => {
    setAuthSessionCookies(
      session.access_token,
      session.refresh_token,
      { domain: cookieDomain },
      previousRefreshToken,
    );
  };

  useEffect(() => {
    let mounted = true;
    let pendingAdoption: SessionAdoptionContext | null = null;
    let pendingRetryTimer: ReturnType<typeof setTimeout> | null = null;
    const adoptionTimers = new Set<ReturnType<typeof setTimeout>>();

    const scheduleAdoptionTimer = (callback: () => void, delay: number) => {
      const timer = setTimeout(() => {
        adoptionTimers.delete(timer);
        callback();
      }, delay);
      adoptionTimers.add(timer);
    };

    const adoptionWasCancelled = (context: SessionAdoptionContext) =>
      hasAuthLogoutIntent() ||
      readCookie(AUTH_LOGOUT_GENERATION_COOKIE) !== context.logoutGeneration;

    const rejectCookieSession = (error: unknown) => {
      console.warn('[auth] newer cookie session rejected, clearing', error);
      clearUser();
      clearAuthCookies({ domain: cookieDomain });
      tripAuthRefreshLatch();
      pendingAdoption = null;
      if (pendingRetryTimer) clearTimeout(pendingRetryTimer);
      pendingRetryTimer = null;
      lastSeenAccessTokenRef.current = null;
      lastSeenRefreshTokenRef.current = null;
      lastSeenUserIdRef.current = null;
      setSession(null);
    };

    const scheduleCookieSessionAdoption = (context: SessionAdoptionContext, attempt: number) => {
      if (adoptionWasCancelled(context)) return;
      const delay = attempt === 0 ? 0 : SESSION_ADOPTION_RETRY_DELAYS_MS[attempt - 1];
      if (delay === undefined) {
        if (hasAuthRefreshAttempt()) {
          scheduleAdoptionTimer(() => void adoptCookieSession(context, attempt), 1_000);
          return;
        }
        const latestSession = readAuthSessionCookies();
        if (!latestSession || latestSession.accessToken === context.previousToken) {
          rejectCookieSession(new Error('Concurrent refresh ended without a committed session'));
          return;
        }
        pendingAdoption = context;
        if (!pendingRetryTimer) {
          pendingRetryTimer = setTimeout(() => {
            pendingRetryTimer = null;
            retryPendingAdoption();
          }, 30_000);
        }
        return;
      }
      scheduleAdoptionTimer(() => void adoptCookieSession(context, attempt), delay);
    };

    const adoptCookieSession = async (context: SessionAdoptionContext, attempt: number) => {
      if (!mounted || adoptionWasCancelled(context)) return;
      const latestSession = readAuthSessionCookies();
      if (!latestSession || latestSession.accessToken === context.previousToken) {
        // The pair writer may still be between its projection-cookie writes
        // and the final commit. Retry only while a newer access projection is
        // still present; an explicit sign-out clears it and stops the loop.
        const projectedAccessToken = readCookie(AUTH_COOKIE);
        if (
          hasAuthRefreshAttempt() ||
          (projectedAccessToken !== undefined && projectedAccessToken !== context.previousToken)
        ) {
          scheduleCookieSessionAdoption(context, attempt + 1);
        } else {
          rejectCookieSession(new Error('Concurrent refresh ended without a committed session'));
        }
        return;
      }

      if (
        readJwtSubject(latestSession.accessToken) !== context.previousUserId ||
        latestSession.previousRefreshToken !== context.previousRefreshToken
      ) {
        rejectCookieSession(new Error('Committed cookie session is not a refresh successor'));
        return;
      }

      let error: unknown;
      try {
        ({ error } = await supabase.auth.setSession({
          access_token: latestSession.accessToken,
          refresh_token: latestSession.refreshToken,
        }));
      } catch (caught) {
        error = caught;
      }

      if (!mounted) return;
      if (adoptionWasCancelled(context)) {
        await supabase.auth.signOut({ scope: 'local' });
        rejectCookieSession(new Error('Cookie session adoption cancelled by explicit logout'));
        return;
      }
      if (!error) {
        pendingAdoption = null;
        if (pendingRetryTimer) clearTimeout(pendingRetryTimer);
        pendingRetryTimer = null;
        return;
      }

      const currentSession = readAuthSessionCookies();
      if (
        currentSession &&
        (currentSession.accessToken !== latestSession.accessToken ||
          currentSession.refreshToken !== latestSession.refreshToken)
      ) {
        scheduleCookieSessionAdoption(context, attempt + 1);
        return;
      }

      if (hasAuthRefreshAttempt()) {
        scheduleCookieSessionAdoption(context, attempt + 1);
        return;
      }

      if (isTerminalSessionAdoptionError(error)) {
        rejectCookieSession(error);
        return;
      }

      console.debug('[auth] failed to adopt newer cookie session transiently', error);
      scheduleCookieSessionAdoption(context, attempt + 1);
    };

    const retryPendingAdoption = () => {
      if (!pendingAdoption || !mounted) return;
      const context = pendingAdoption;
      pendingAdoption = null;
      if (pendingRetryTimer) clearTimeout(pendingRetryTimer);
      pendingRetryTimer = null;
      scheduleCookieSessionAdoption(context, 0);
    };

    const retryPendingAdoptionWhenVisible = () => {
      if (document.visibilityState === 'visible') retryPendingAdoption();
    };

    window.addEventListener('online', retryPendingAdoption);
    window.addEventListener('focus', retryPendingAdoption);
    document.addEventListener('visibilitychange', retryPendingAdoptionWhenVisible);

    // ── 1. Wait for Supabase to resolve the initial session ──────────
    // getSession() awaits the same _initialize() promise as onAuthStateChange,
    // so by the time it resolves the hash tokens (if any) have been processed.
    // We use this purely as the "ready" gate AND to hydrate the session atom
    // before the first render so RequireAuth sees the correct value.
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (!mounted) return;
        console.debug(
          '[auth] initial session resolved',
          session ? 'authenticated' : 'unauthenticated',
        );

        // Dev-only server-side validation. getSession() only validates the
        // locally-stored JWT structure — it does not verify that the sub
        // claim still resolves on the server. After `supabase db reset
        // --local` a stored token can outlive its auth.users row, leaving
        // the app looping on 403s against every RLS'd endpoint. In
        // production this proactive check causes worse problems: any
        // transient `/auth/v1/user` failure (429 rate limit, network blip,
        // 5xx) gets misinterpreted as session-invalid and signs the user
        // out, including immediately after a successful magic-link login.
        let validSession = session;
        if (
          hasAuthLogoutIntent() &&
          validSession &&
          isInitialAuthCallback(validSession.access_token, validSession.refresh_token)
        ) {
          clearAuthLogoutIntent({ domain: cookieDomain });
        } else if (hasAuthLogoutIntent()) {
          if (session) await supabase.auth.signOut({ scope: 'local' });
          clearUser();
          clearAuthCookies({ domain: cookieDomain });
          validSession = null;
        } else if (import.meta.env.DEV && session) {
          const { error: userError } = await supabase.auth.getUser();
          // Only treat true auth rejections (user deleted, token revoked)
          // as session-invalid. 429 / 5xx / network errors are transient
          // and must not clear a valid session.
          if (userError && (userError.status === 401 || userError.status === 403)) {
            console.warn('[auth] stored session rejected by server, clearing', userError);
            await supabase.auth.signOut({ scope: 'local' });
            clearUser();
            clearAuthCookies({ domain: cookieDomain });
            validSession = null;
          } else if (userError) {
            console.warn('[auth] getUser failed transiently — keeping session', userError);
          }
        }

        if (!mounted) return;
        if (validSession) {
          setUser(validSession);
          syncCookies(validSession);
        }
        setSession(validSession);
        // Seed lastSeenAccessTokenRef so the SIGNED_OUT race-loss check
        // below has a baseline to compare against. supabase-auth-js's
        // INITIAL_SESSION event also writes this ref, but its delivery
        // is racy w.r.t. this getSession().then() chain — and the
        // listener relies on this ref being correct from the moment
        // isAuthReady flips true (otherwise a user-clicked sign-out
        // would be mistaken for a race-loss).
        lastSeenAccessTokenRef.current = validSession?.access_token ?? null;
        lastSeenRefreshTokenRef.current = validSession?.refresh_token ?? null;
        lastSeenUserIdRef.current = validSession?.user.id ?? null;
        isAuthReadyRef.current = true;
        setIsAuthReady(true);
      })
      .catch((err) => {
        console.error('[auth] getSession failed — falling through to login', err);
        if (mounted) {
          isAuthReadyRef.current = true;
          setIsAuthReady(true);
        }
      });

    // ── 2. Subscribe to subsequent auth state changes ────────────────
    // After the initial session, this handles: TOKEN_REFRESHED, SIGNED_OUT,
    // SIGNED_IN (e.g. from another tab), USER_UPDATED, etc.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // INITIAL_SESSION is owned by the getSession().then() chain above —
      // letting the listener also write the atom briefly publishes the
      // pre-validation session, which makes LoginPage's <Navigate> redirect
      // away from /login before validation finishes, and then back to /login
      // when it does. That round-trip is the "flash" users see.
      if (event === 'INITIAL_SESSION') {
        if (
          hasAuthLogoutIntent() &&
          (!session || !isInitialAuthCallback(session.access_token, session.refresh_token))
        ) {
          lastSeenAccessTokenRef.current = null;
          lastSeenRefreshTokenRef.current = null;
          lastSeenUserIdRef.current = null;
        } else {
          lastSeenAccessTokenRef.current = session?.access_token ?? null;
          lastSeenRefreshTokenRef.current = session?.refresh_token ?? null;
          lastSeenUserIdRef.current = session?.user.id ?? null;
        }
        return;
      }

      const nextToken = session?.access_token ?? null;
      const prevToken = lastSeenAccessTokenRef.current;

      if (session && hasAuthLogoutIntent()) {
        console.debug('[auth] ignoring session event while explicit logout is active');
        return;
      }

      // Deduplicate rapid identical events. Floods of SIGNED_OUT with the
      // same null token can arrive via BroadcastChannel from sibling tabs
      // during cross-tab refresh races; we only want to act when the
      // session actually changes.
      if (event !== 'SIGNED_OUT' && nextToken === prevToken) {
        return;
      }

      console.debug('[auth] state change:', event, session ? 'authenticated' : 'null');

      if (session) {
        setUser(session);
        syncCookies(
          session,
          event === 'TOKEN_REFRESHED' ? (lastSeenRefreshTokenRef.current ?? undefined) : undefined,
        );
        // Re-arm api-client after a fresh sign-in or token refresh. The
        // latch persists across re-logins otherwise (module state survives
        // until a full page reload), so after a SIGNED_OUT-triggered trip
        // the user would log back in but every authenticated call would
        // short-circuit with `auth_refresh_failed`.
        resetAuthRefreshLatch();
        lastSeenRefreshTokenRef.current = session.refresh_token;
        lastSeenUserIdRef.current = session.user.id;
      }

      // Only clear cookies & user state on explicit sign-out AFTER the initial
      // auth handshake. A startup-race SIGNED_OUT broadcast from a sibling
      // tab would otherwise wipe a valid session this tab is still loading.
      if (event === 'SIGNED_OUT' && isAuthReadyRef.current) {
        if (hasAuthLogoutIntent()) {
          clearUser();
          clearAuthCookies({ domain: cookieDomain });
          tripAuthRefreshLatch();
          lastSeenAccessTokenRef.current = null;
          lastSeenRefreshTokenRef.current = null;
          lastSeenUserIdRef.current = null;
          setSession(null);
          return;
        }
        // Race-loss check: supabase-auth-js's auto-refresh ticker and
        // api-client's customFetch refresh path both consume the single-use
        // artic_refresh_token. If auth-js loses, customFetch has already
        // written a *fresher* access_token to artic_token before auth-js's
        // failed refresh broadcasts SIGNED_OUT. The tell: the cookie value
        // differs from the access_token auth-js was last working with
        // (prevToken). In that case the user is still authenticated, so we
        // keep the session.
        //
        // For a user-initiated signOut() no parallel refresh happened, so
        // the cookie still holds the same token auth-js had — cookieToken
        // matches prevToken and we proceed with the teardown. An earlier
        // version of this check only tested cookie *presence*, which
        // always bailed on user-initiated sign-outs because the cookie
        // hadn't been cleared yet — this listener is what clears it.
        const cookieToken = readCookie(AUTH_COOKIE);
        if ((cookieToken && cookieToken !== prevToken) || hasAuthRefreshAttempt()) {
          console.warn(
            '[auth] SIGNED_OUT during a concurrent refresh — waiting for the committed session',
          );
          // The winning refresh may have happened in Neo, which cannot update
          // this tab's in-memory Supabase client. Rehydrate after the auth
          // callback unwinds; invoking another auth method synchronously from
          // onAuthStateChange can deadlock auth-js's internal lock.
          const previousRefreshToken = lastSeenRefreshTokenRef.current;
          const previousUserId = lastSeenUserIdRef.current;
          if (prevToken && previousRefreshToken && previousUserId) {
            scheduleCookieSessionAdoption(
              {
                previousToken: prevToken,
                previousRefreshToken,
                previousUserId,
                logoutGeneration: readCookie(AUTH_LOGOUT_GENERATION_COOKIE),
              },
              0,
            );
          } else {
            rejectCookieSession(
              new Error('Cannot identify the session that lost the refresh race'),
            );
          }
          return;
        }
        clearUser();
        clearAuthCookies({ domain: cookieDomain });
        // Bridge the supabase-side failure to api-client's latch. Without
        // this, every `getSession()` caller in the hub (~30 of them) fires
        // a fresh refresh attempt before customFetch's own refresh path
        // happens to be exercised and trip the latch from the inside.
        // Tripping it here turns subsequent `/auth/v1/token` calls into
        // instant synthetic 401s via `guardedFetch`, killing the storm
        // after one real failure. Race-loss bails above so we never trip
        // on a concurrent-refresh false positive.
        tripAuthRefreshLatch();
      }

      lastSeenAccessTokenRef.current = nextToken;
      lastSeenRefreshTokenRef.current = session?.refresh_token ?? null;
      lastSeenUserIdRef.current = session?.user.id ?? null;
      setSession(session);
    });

    // ── 3. Safety net — unblock UI if init hangs ─────────────────────
    const timeout = setTimeout(() => {
      if (!mounted) return;
      if (!isAuthReadyRef.current) {
        console.warn('[auth] init timed out — rendering app without session');
        isAuthReadyRef.current = true;
        setIsAuthReady(true);
      }
    }, AUTH_READY_TIMEOUT_MS);

    return () => {
      mounted = false;
      if (pendingRetryTimer) clearTimeout(pendingRetryTimer);
      for (const timer of adoptionTimers) clearTimeout(timer);
      window.removeEventListener('online', retryPendingAdoption);
      window.removeEventListener('focus', retryPendingAdoption);
      document.removeEventListener('visibilitychange', retryPendingAdoptionWhenVisible);
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  if (!isAuthReady) return null;

  return (
    <>
      <NuqsAdapter>
        <AppRouter />
      </NuqsAdapter>
      <ReactQueryDevtools initialIsOpen={false} />
    </>
  );
}

export default App;
